/** Shared pieces of the drift-section charts (max-pain drift + per-strike
 * OI change): canvas dimensions, mode labels, path/axis helpers. Kept out
 * of the chart components so both files stay under the size lint. */

import {
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { cn } from "@/lib/cn";
import { xScale, yScale, type Bounds } from "@/components/Reports/analyticsMath";
import type { DriftMode } from "@/types/market";

export const W = 640;
export const H = 180;
export const POLL_MS = 60_000;

export const MODE_LABELS: Record<DriftMode, string> = {
  intraday: "Intraday",
  day: "Day-over-Day",
};

/** One toggle for the whole trends card — it drives the OI columns AND
 * the max-pain chart, so OptionsTab renders it above both. */
export function ModeToggle({
  value,
  onChange,
}: Readonly<{ value: DriftMode; onChange: (mode: DriftMode) => void }>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line bg-surface-1 p-0.5">
      {(Object.keys(MODE_LABELS) as DriftMode[]).map((mode) => (
        <button
          key={mode}
          type="button"
          className={cn(
            "rounded-ctl px-2 py-1 text-xs font-medium transition-colors",
            mode === value ? "bg-accent-bg text-accent" : "text-ink-secondary hover:text-ink",
          )}
          onClick={() => {
            onChange(mode);
          }}
        >
          {MODE_LABELS[mode]}
        </button>
      ))}
    </div>
  );
}

export function deltaTone(delta: number): string {
  if (delta > 0) return "text-success";
  if (delta < 0) return "text-danger";
  return "text-ink-secondary";
}

export function svgPath(values: readonly number[], bounds: Bounds): string {
  return values
    .map(
      (v, i) =>
        `${i === 0 ? "M" : "L"}${xScale(i, values.length, W).toFixed(1)} ${yScale(v, bounds, H).toFixed(1)}`,
    )
    .join(" ");
}

/** IST clock time for intraday points; IST date for day points. */
export function axisLabel(at: string | undefined, mode: DriftMode): string {
  if (at === undefined) return "";
  const when = new Date(at);
  if (mode === "intraday") {
    return when.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    });
  }
  return when.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    timeZone: "Asia/Kolkata",
  });
}

export function ZeroDash({ y }: Readonly<{ y: number }>): ReactElement {
  return (
    <line
      x1={0}
      y1={y}
      x2={W}
      y2={y}
      stroke="var(--line, #e5e7eb)"
      strokeWidth={1}
      strokeDasharray="3 3"
    />
  );
}

/** Edge ticks hug the chart edges; interior ticks center on their line. */
function tickAlign(i: number, n: number): string {
  if (i === 0) return "absolute";
  if (i === n - 1) return "absolute -translate-x-full";
  return "absolute -translate-x-1/2";
}

/** ~`target` nice round tick values spanning the bounds (1/2/2.5/5×10^k
 * steps), for y-axis gridlines and labels. */
export function niceTicks(bounds: Bounds, target = 4): number[] {
  const span = bounds.max - bounds.min;
  if (span <= 0 || !Number.isFinite(span)) return [];
  const raw = span / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  const out: number[] = [];
  for (let t = Math.ceil(bounds.min / step) * step; t <= bounds.max + step / 1e6; t += step) {
    out.push(t);
  }
  return out;
}

/** Horizontal gridlines inside the chart SVG, one per tick value. */
export function YGrid({
  values,
  bounds,
  height,
}: Readonly<{ values: readonly number[]; bounds: Bounds; height: number }>): ReactElement {
  return (
    <>
      {values.map((v) => (
        <line
          key={v}
          x1={0}
          y1={yScale(v, bounds, height)}
          x2={W}
          y2={yScale(v, bounds, height)}
          stroke="var(--line, #e5e7eb)"
          strokeWidth={0.5}
          opacity={0.6}
        />
      ))}
    </>
  );
}

/** Tick-value labels overlaid on one edge of the chart (left by
 * default; the OI columns put theirs on the right, Sensibull-style).
 * HTML, not SVG text — preserveAspectRatio=none would stretch glyphs.
 * The chart's wrapper must be `relative`. */
export function YAxisLabels({
  values,
  bounds,
  height,
  format,
  side = "left",
}: Readonly<{
  values: readonly number[];
  bounds: Bounds;
  height: number;
  format: (v: number) => string;
  side?: "left" | "right";
}>): ReactElement {
  return (
    <div className="pointer-events-none absolute inset-0 font-mono text-[9px] text-ink-muted">
      {values.map((v) => (
        <span
          key={v}
          className={cn("absolute -translate-y-1/2", side === "left" ? "left-0.5" : "right-0.5")}
          style={{ top: `${String((yScale(v, bounds, height) / height) * 100)}%` }}
        >
          {format(v)}
        </span>
      ))}
    </div>
  );
}

/** Keep the LAST `1/zoom` of a time series (zoom anchored to the most
 * recent data), never fewer than 2 points. */
export function zoomSlice<T>(points: readonly T[], zoom: number): readonly T[] {
  if (zoom <= 1 || points.length <= 2) return points;
  return points.slice(-Math.max(Math.ceil(points.length / zoom), 2));
}

export interface ChartHover {
  frac: number | null;
  /** Vertical position (0 = top .. 1 = bottom) — lets a chart pick the
   * LINE nearest the cursor, not just the time column. */
  fracY: number | null;
  ref: RefObject<HTMLDivElement>;
  onMove: (e: MouseEvent<HTMLDivElement>) => void;
  onLeave: () => void;
}

/** Hover position (0..1 both axes) over a chart wrapper element. */
export function useChartHover(): ChartHover {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  return {
    frac: pos?.x ?? null,
    fracY: pos?.y ?? null,
    ref,
    onMove: (e: MouseEvent<HTMLDivElement>) => {
      const rect = ref.current?.getBoundingClientRect();
      if (rect === undefined || rect.width <= 0 || rect.height <= 0) return;
      setPos({
        x: Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1),
        y: Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1),
      });
    },
    onLeave: () => {
      setPos(null);
    },
  };
}

/** Nearest series index for a hover fraction; null while not hovering. */
export function hoverIndex(frac: number | null, n: number): number | null {
  if (frac === null || n < 2) return null;
  return Math.min(n - 1, Math.max(0, Math.round(frac * (n - 1))));
}

/** Vertical crosshair at one series index. */
export function HoverCrosshair({ i, n }: Readonly<{ i: number; n: number }>): ReactElement {
  const x = xScale(i, n, W);
  return (
    <line
      x1={x}
      y1={0}
      x2={x}
      y2={H}
      stroke="var(--ink, #111827)"
      strokeWidth={1}
      strokeDasharray="2 2"
      opacity={0.6}
    />
  );
}

/** Floating tooltip box that follows the hover fraction and flips sides
 * past mid-chart so it never leaves the canvas. */
export function ChartTipBox({
  frac,
  children,
}: Readonly<{ frac: number; children: ReactNode }>): ReactElement {
  const flip = frac > 0.55;
  return (
    <div
      data-testid="chart-tip"
      className="pointer-events-none absolute top-1 z-10 min-w-32 rounded-lg border border-line bg-surface-0 p-2 text-[11px] shadow-lg"
      style={
        flip
          ? { right: `${String((1 - frac) * 100)}%`, marginRight: 8 }
          : { left: `${String(frac * 100)}%`, marginLeft: 8 }
      }
    >
      {children}
    </div>
  );
}

/** Time ticks under a chart: up to four evenly spaced points, labeled
 * IST clock time (intraday) or date (day mode). Edge labels stay inside
 * the chart width instead of centering off it. */
export function XAxisLabels({
  series,
  mode,
}: Readonly<{ series: readonly { at: string }[]; mode: DriftMode }>): ReactElement | null {
  const n = series.length;
  if (n < 2) return null;
  const count = Math.min(4, n);
  const idxs = [
    ...new Set(Array.from({ length: count }, (_, k) => Math.round((k * (n - 1)) / (count - 1)))),
  ];
  return (
    <div className="relative h-4 overflow-hidden font-mono text-[9px] text-ink-muted">
      {idxs.map((i) => (
        <span
          key={i}
          className={tickAlign(i, n)}
          style={{ left: `${String((xScale(i, n, W) / W) * 100)}%` }}
        >
          {axisLabel(series[i]?.at, mode)}
        </span>
      ))}
    </div>
  );
}
